/**
 * CSV文字列のパース
 *
 * 受け取るのは points_csv.build_csv の出力だけで、生成側の設定は固定されている
 * - 区切りはカンマ、引用符は二重引用符、行区切りはLF、引用符のエスケープは倍化
 * - 値に , " CR LF のいずれかを含むときだけクォートされる（PythonのQUOTE_MINIMAL）
 *
 * この範囲しか来ないので汎用のCSVライブラリは使わない
 * 生成側の設定を変えるときはここも合わせる
 */

/**
 * 黙って読める範囲だけ返すと、列ずれに気づけないまま絞り込み結果が変わる
 */
export class CsvParseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CsvParseError'
  }
}

/**
 * CSV文字列を二次元配列にする
 *
 * クォートの有無によらず空フィールドは空文字になる
 * 空文字の意味づけ（キーを作らない・nullにする）は呼び出し側が決める
 *
 * @param text CSV文字列
 * @returns 行ごとのフィールドの配列
 * @throws CsvParseError クォートが閉じていない、クォートの後に値が続く
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []

  if (text === '') {
    return rows
  }

  let row: string[] = []
  let field = ''
  // 末尾の行区切りを空行にしないために持つ
  let started = false
  let quoted = false
  // `"a"b` のような値を検出するために持つ
  let closed = false
  let index = 0

  while (index < text.length) {
    const char = text[index]

    if (quoted) {
      if (char !== '"') {
        field += char
        index += 1
        continue
      }

      if (text[index + 1] === '"') {
        field += '"'
        index += 2
        continue
      }

      quoted = false
      closed = true
      index += 1
      continue
    }

    if (char === '"') {
      if (field !== '' || closed) {
        throw new CsvParseError(`unexpected quote at ${index}`)
      }

      quoted = true
      started = true
      index += 1
      continue
    }

    if (char === ',') {
      row.push(field)
      field = ''
      started = true
      closed = false
      index += 1
      continue
    }

    if (char === '\n' || char === '\r') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
      started = false
      closed = false
      index += char === '\r' && text[index + 1] === '\n' ? 2 : 1
      continue
    }

    if (closed) {
      throw new CsvParseError(`unexpected character after quote at ${index}`)
    }

    field += char
    started = true
    index += 1
  }

  if (quoted) {
    throw new CsvParseError('unterminated quote')
  }

  if (started) {
    row.push(field)
    rows.push(row)
  }

  return rows
}
